import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { ChecklistCard } from '../components/ChecklistCard';
import { ChevronLeft, ChevronRight, Plus, Trash2, Pencil, Check, X, Repeat, CalendarPlus } from 'lucide-react';

const CATEGORIES = ['Vorbereitung Mittag', 'Küche', 'Service', 'Abschluss abends', 'Allgemein'];
const ADMIN_ROLES = ['super_admin', 'admin', 'restaurant_leader'];
const WEEKDAYS = [[0, 'Mo'], [1, 'Di'], [2, 'Mi'], [3, 'Do'], [4, 'Fr'], [5, 'Sa'], [6, 'So']];

function weekdayLabel(wds) {
  if (!wds || wds.length === 0 || wds.length >= 7) return 'täglich';
  return [...wds].sort((a, b) => a - b).map(d => WEEKDAYS[d][1]).join(', ');
}

function WeekdayPicker({ value = [], onChange, testId }) {
  const toggle = (d) => {
    onChange(value.includes(d) ? value.filter(x => x !== d) : [...value, d]);
  };
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testId}>
      {WEEKDAYS.map(([d, label]) => {
        const on = value.includes(d);
        return (
          <button
            key={d}
            type="button"
            onClick={() => toggle(d)}
            data-testid={`${testId}-${d}`}
            aria-pressed={on}
            className={`w-8 h-8 rounded text-xs font-medium transition border ${
              on ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'bg-white text-[#5C5C59] border-[#E5E5E0] hover:border-[#1C1C19]'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(ds) {
  try {
    return new Date(ds + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return ds; }
}

function shiftDate(ds, days) {
  const d = new Date(ds + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AdminChecklist() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [date, setDate] = useState(todayStr());
  const [reloadKey, setReloadKey] = useState(0);
  const bump = () => setReloadKey(k => k + 1);

  return (
    <div className="space-y-6" data-testid="admin-checklist">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl text-[#1C1C19]">Checkliste</h1>
          <p className="text-sm text-[#5C5C59]">Aufgaben für den Tag – von jedem Mitarbeiter abhakbar.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setDate(shiftDate(date, -1))} data-testid="checklist-prev-day" className="p-2 rounded border border-[#E5E5E0] hover:bg-[#F9F9F7]">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <input
            type="date" value={date} onChange={e => setDate(e.target.value)}
            data-testid="checklist-date-input"
            className="px-3 py-2 border border-[#E5E5E0] rounded text-sm"
          />
          <button onClick={() => setDate(shiftDate(date, 1))} data-testid="checklist-next-day" className="p-2 rounded border border-[#E5E5E0] hover:bg-[#F9F9F7]">
            <ChevronRight className="w-4 h-4" />
          </button>
          {date !== todayStr() && (
            <button onClick={() => setDate(todayStr())} data-testid="checklist-today-btn" className="px-3 py-2 rounded border border-[#E5E5E0] hover:bg-[#F9F9F7] text-sm">Heute</button>
          )}
        </div>
      </div>

      <p className="text-sm font-medium text-[#1C1C19] capitalize">{fmtDate(date)}</p>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <ChecklistCard date={date} title="Aufgaben" reloadKey={reloadKey} />

        {isAdmin && (
          <div className="space-y-6">
            <OneTimeAdder date={date} onAdded={bump} />
            <TemplateManager onChanged={bump} />
          </div>
        )}
      </div>
    </div>
  );
}

function CategorySelect({ value, onChange, testId }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testId} className="px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}

// Einmalige Aufgabe für das gewählte Datum
function OneTimeAdder({ date, onAdded }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Allgemein');
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post('/checklist/tasks', { date, title: title.trim(), category });
      setTitle('');
      toast.success('Aufgabe hinzugefügt');
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-4">
      <h3 className="font-display text-base text-[#1C1C19] flex items-center gap-2 mb-3">
        <CalendarPlus className="w-4 h-4" /> Aufgabe nur für diesen Tag
      </h3>
      <form onSubmit={add} className="flex flex-col gap-2">
        <input
          value={title} onChange={e => setTitle(e.target.value)}
          placeholder="z. B. Getränke-Lieferung annehmen"
          data-testid="onetime-title-input"
          className="px-3 py-2 border border-[#E5E5E0] rounded text-sm"
        />
        <div className="flex gap-2">
          <CategorySelect value={category} onChange={setCategory} testId="onetime-category-select" />
          <button type="submit" disabled={saving || !title.trim()} data-testid="onetime-add-btn" className="btn-primary px-4 py-2 rounded text-sm flex items-center gap-1 disabled:opacity-50">
            <Plus className="w-4 h-4" /> Hinzufügen
          </button>
        </div>
      </form>
    </div>
  );
}

// Wiederkehrende Standard-Aufgaben (Vorlagen) verwalten
function TemplateManager({ onChanged }) {
  const [templates, setTemplates] = useState([]);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Allgemein');
  const [weekdays, setWeekdays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState({ title: '', category: 'Allgemein', weekdays: [] });

  const load = useCallback(async () => {
    try {
      const r = await api.get('/checklist/templates');
      setTemplates(r.data?.templates || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post('/checklist/templates', { title: title.trim(), category, weekdays });
      setTitle(''); setWeekdays([]);
      toast.success('Vorlage hinzugefügt');
      load(); onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  const saveEdit = async (t) => {
    if (!editVal.title.trim()) return;
    try {
      await api.put(`/checklist/templates/${t.id}`, { title: editVal.title.trim(), category: editVal.category, weekdays: editVal.weekdays, position: t.position, active: t.active });
      setEditId(null);
      load(); onChanged();
    } catch { toast.error('Fehler'); }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/checklist/templates/${t.id}`, { title: t.title, category: t.category, weekdays: t.weekdays || [], position: t.position, active: !t.active });
      load(); onChanged();
    } catch { toast.error('Fehler'); }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/checklist/templates/${t.id}`);
      load(); onChanged();
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="template-manager">
      <h3 className="font-display text-base text-[#1C1C19] flex items-center gap-2 mb-1">
        <Repeat className="w-4 h-4" /> Wiederkehrende Aufgaben
      </h3>
      <p className="text-xs text-[#9CA3AF] mb-3">Erscheinen jeden Tag automatisch – optional auf bestimmte Wochentage einschränken.</p>

      <form onSubmit={add} className="flex flex-col gap-2 mb-4">
        <input
          value={title} onChange={e => setTitle(e.target.value)}
          placeholder="z. B. Tische eindecken"
          data-testid="template-title-input"
          className="px-3 py-2 border border-[#E5E5E0] rounded text-sm"
        />
        <div className="flex gap-2 flex-wrap items-center">
          <CategorySelect value={category} onChange={setCategory} testId="template-category-select" />
          <button type="submit" disabled={saving || !title.trim()} data-testid="template-add-btn" className="btn-primary px-4 py-2 rounded text-sm flex items-center gap-1 disabled:opacity-50">
            <Plus className="w-4 h-4" /> Vorlage
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#5C5C59] w-20 shrink-0">Wochentage</span>
          <WeekdayPicker value={weekdays} onChange={setWeekdays} testId="template-weekdays" />
        </div>
        <p className="text-[11px] text-[#9CA3AF] ml-[88px] -mt-1">Keine Auswahl = täglich</p>
      </form>

      {templates.length === 0 ? (
        <p className="text-sm text-[#9CA3AF] py-2">Noch keine Vorlagen.</p>
      ) : (
        <div className="space-y-1">
          {templates.map(t => (
            <div key={t.id} data-testid={`template-row-${t.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F9F9F7]">
              {editId === t.id ? (
                <div className="flex-1 flex flex-col gap-2 py-1">
                  <div className="flex items-center gap-2">
                    <input
                      value={editVal.title} onChange={e => setEditVal({ ...editVal, title: e.target.value })}
                      data-testid={`template-edit-title-${t.id}`}
                      className="flex-1 px-2 py-1 border border-[#E5E5E0] rounded text-sm"
                    />
                    <CategorySelect value={editVal.category} onChange={v => setEditVal({ ...editVal, category: v })} testId={`template-edit-category-${t.id}`} />
                    <button onClick={() => saveEdit(t)} data-testid={`template-save-${t.id}`} className="p-1 text-[#22C55E] hover:bg-[#22C55E]/10 rounded"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditId(null)} className="p-1 text-[#5C5C59] hover:bg-[#F0F0ED] rounded"><X className="w-4 h-4" /></button>
                  </div>
                  <WeekdayPicker value={editVal.weekdays} onChange={v => setEditVal({ ...editVal, weekdays: v })} testId={`template-edit-weekdays-${t.id}`} />
                </div>
              ) : (
                <>
                  <button
                    onClick={() => toggleActive(t)}
                    data-testid={`template-toggle-active-${t.id}`}
                    title={t.active ? 'Aktiv – klicken zum Deaktivieren' : 'Inaktiv – klicken zum Aktivieren'}
                    className={`w-2 h-2 rounded-full shrink-0 ${t.active ? 'bg-[#22C55E]' : 'bg-[#D1D5DB]'}`}
                  />
                  <span className={`flex-1 text-sm ${t.active ? 'text-[#1C1C19]' : 'text-[#9CA3AF] line-through'}`}>{t.title}</span>
                  <span className="text-[10px] text-[#5C5C59] bg-[#F0F0ED] px-1.5 py-0.5 rounded" data-testid={`template-weekday-label-${t.id}`}>{weekdayLabel(t.weekdays)}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{t.category}</span>
                  <button onClick={() => { setEditId(t.id); setEditVal({ title: t.title, category: t.category, weekdays: t.weekdays || [] }); }} data-testid={`template-edit-${t.id}`} className="p-1 text-[#5C5C59] hover:text-[#1C1C19]"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(t)} data-testid={`template-delete-${t.id}`} className="p-1 text-[#5C5C59] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
